/**
 * SearchSeedCredentialsEditor — the PLATFORM-plane search fallback keys (S5b of
 * `ai-docs/web-search-providers-implementation-plan.md`; plan of record for this
 * slice: `ai-docs/search-providers-portal-ui-miniplan.md`).
 *
 * Sibling of `SeedCredentialsEditor`, deliberately NOT merged with it: the
 * `searchSeedCredentials` store is separate from `seedCredentials` on the
 * server, and LLM keys and search keys have different rotation lifetimes. Two
 * editors that look alike but never share state.
 *
 * ⚠ PLATFORM-ADMIN ONLY, and that is why it lives here rather than in
 * `@lexiun/org-admin`. The shared bundle ships to ORG admins through LexIDE, and
 * a platform-scoped capability must never enter it. The "Testar" action belongs
 * to this component for the same reason — `POST /api/search-providers/:orgId/test`
 * is `requirePlatformAdmin()`, so the button would 403 for the org-admin audience
 * even if the invariant allowed it (agreed with `search-providers`, handshake
 * `20260813T222927Z`).
 *
 * Masking contract: `GET` returns metadata only — never ciphertext, never a
 * sentinel. A provider ABSENT from the map is unconfigured; the map never
 * carries a placeholder, so "no key" and "key present" cannot be confused.
 */

import { useCallback, useEffect, useState } from "react";
import { Eye, EyeOff, KeyRound, Loader2, RefreshCw } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { usePlatformApi } from "@/hooks/usePlatformApi";
import type { SearchSeedCredentialMasked } from "@/lib/platform-api";

/** Providers offered a row even when unconfigured, so an operator can set one.
 *  Mirrors the server's KNOWN_PROVIDERS; any provider the server returns that
 *  is not listed here is unioned in at render time rather than hidden. */
const KNOWN_PROVIDERS = ["brave", "perplexity"] as const;

interface RowState {
  input: string;
  reveal: boolean;
  saving: boolean;
  error: string | null;
  savedAt: number | null;
}

const EMPTY_ROW: RowState = { input: "", reveal: false, saving: false, error: null, savedAt: null };

export function SearchSeedCredentialsEditor() {
  const api = usePlatformApi();
  const [masked, setMasked] = useState<Record<string, SearchSeedCredentialMasked>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rows, setRows] = useState<Record<string, RowState>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api.getSearchSeedCredentials();
      setMasked(res.data ?? {});
    } catch (e) {
      // A failed READ must not render as "nothing configured" — that is the
      // whole defect class this console keeps hitting. Keep the error visible
      // and do NOT clear `masked`.
      setLoadError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const providers = Array.from(
    new Set<string>([...KNOWN_PROVIDERS, ...Object.keys(masked)]),
  ).sort();

  async function save(provider: string) {
    const row = rows[provider] ?? EMPTY_ROW;
    const apiKey = row.input.trim();
    if (!apiKey) return;
    setRows((r) => ({ ...r, [provider]: { ...row, saving: true, error: null } }));
    try {
      const res = await api.setSearchSeedCredential(provider, apiKey);
      setMasked((m) => ({ ...m, [provider]: res.data }));
      setRows((r) => ({
        ...r,
        [provider]: { ...EMPTY_ROW, savedAt: Date.now() },
      }));
    } catch (e) {
      setRows((r) => ({
        ...r,
        [provider]: {
          ...(r[provider] ?? EMPTY_ROW),
          saving: false,
          error: e instanceof Error ? e.message : String(e),
        },
      }));
    }
  }

  return (
    <Card data-testid="search-seed-credentials">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <KeyRound className="h-4 w-4" /> Search fallback credentials
        </CardTitle>
        <Button variant="ghost" size="sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-slate-500">
          Platform-wide fallback keys used when an organization has not set its own.
          Stored in <code>searchSeedCredentials</code> — a separate store from the LLM
          seed credentials above, never merged.
        </p>

        {loadError && (
          /* Explicitly NOT an empty state: we could not read the store, which is
             a different fact from "no keys are configured". */
          <p className="text-sm text-red-600 dark:text-red-400" data-testid="search-seed-load-error">
            Could not read the search fallback credentials — {loadError}. This is{" "}
            <strong>not</strong> a statement that none are configured.
          </p>
        )}

        {loading && !loadError ? (
          <p className="text-sm text-slate-500" data-testid="search-seed-loading">
            <Loader2 className="mr-1 inline h-3.5 w-3.5 animate-spin" /> Loading…
          </p>
        ) : (
          <div className="space-y-2">
            {providers.map((provider) => {
              const entry = masked[provider];
              const row = rows[provider] ?? EMPTY_ROW;
              return (
                <div
                  key={provider}
                  className="rounded-md border border-slate-200 p-3 dark:border-slate-700"
                  data-testid={`search-seed-row-${provider}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm">{provider}</span>
                    {entry ? (
                      <span
                        className="text-xs text-green-600 dark:text-green-400"
                        data-testid={`search-seed-configured-${provider}`}
                      >
                        configured · set by {entry.setBy}
                      </span>
                    ) : (
                      <span
                        className="text-xs text-slate-400"
                        data-testid={`search-seed-unset-${provider}`}
                      >
                        not configured
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      type={row.reveal ? "text" : "password"}
                      // Secret field, not a login password — see the note in
                      // PlatformSecuritySettings.tsx. "new-password" is what
                      // browsers honour; "off" is ignored on password inputs.
                      autoComplete="new-password"
                      value={row.input}
                      placeholder={entry ? "Enter a new key to rotate" : "Enter API key"}
                      onChange={(e) =>
                        setRows((r) => ({
                          ...r,
                          [provider]: { ...(r[provider] ?? EMPTY_ROW), input: e.target.value },
                        }))
                      }
                      className="flex-1 rounded border border-slate-300 px-2 py-1 font-mono text-xs dark:border-slate-600 dark:bg-slate-900"
                      data-testid={`search-seed-input-${provider}`}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setRows((r) => ({
                          ...r,
                          [provider]: {
                            ...(r[provider] ?? EMPTY_ROW),
                            reveal: !(r[provider] ?? EMPTY_ROW).reveal,
                          },
                        }))
                      }
                    >
                      {row.reveal ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </Button>
                    <Button
                      size="sm"
                      disabled={row.saving || row.input.trim() === ""}
                      onClick={() => void save(provider)}
                      data-testid={`search-seed-save-${provider}`}
                    >
                      {row.saving ? "Saving…" : entry ? "Rotate" : "Set"}
                    </Button>
                  </div>
                  {row.error && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">{row.error}</p>
                  )}
                  {row.savedAt && !row.error && (
                    <p className="mt-1 text-xs text-green-600 dark:text-green-400">Saved.</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
